import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DynamicForm from "../../stories/Forms/DynamicForm";
import {
  activateKitFields,
  bloodFields,
  dnaFields,
  shFields,
} from "../../stories/Forms/fields";
import useApis from "../../services/useAPI";
import { barcodeValidationSchema } from "../../stories/Forms/validationSchema";
import { ErrorDisplay } from "../Errors/ErrorDisplay";
import { networkError, logoutUrl } from "../../utils/constants";
import StaticModal from "../../stories/Modal/StaticModal";
import Cookies from "js-cookie";
import {
  formatTrackingNumber,
  calculateProgressSteps,
  updateProgressAfterCompletion,
} from "../../utils/utils";
import axios from "axios";

import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { XSquare } from "react-bootstrap-icons";
import BarcodeConfirmationModal from "../../stories/Modal/BarcodeConfirmationModal";
import InfoIcon from "../../assets/icons/info-icon.svg"

/**
 * EnterBarcode component
 *
 * This component manages the input of a barcode and tracking number
 * for activating test kits. It handles validation based on the test type,
 * fetches necessary data from the server, and submits the activation request.
 * Depending on the results, it navigates to the appropriate next step.
 *
 * @returns {JSX.Element} The rendered component for entering barcode and tracking number.
 */

let barcodeData;

const EnterBarcode = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { callApi, isLoading, serverError, resultGet, resultPost } = useApis();
  const [error, setError] = useState("");
  const [data, setData] = useState();
  const [show, setShow] = useState(false);
  const [isConsented, setIsConsented] = useState(false);
  const [consentSubmitted, setConsentSubmitted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null); // ← holds form values while waiting for confirmation
  const auth = useAuth();

  const handleClick = () => {
    if (resultGet.label_issue && !isConsented) {
      setIsConsented(true);
      setShow(false);
    } else if (!resultGet.label_issue) {
      setIsConsented(false);
      setShow(false);
    }
  };


  /**
   * Initialize progress steps when component mounts
   */
  useEffect(() => {
    // Initialize with default progress steps if not already set
    if (!auth?.progressSteps?.totalSteps) {
      const initialProgressSteps = calculateProgressSteps(
        resultGet,
        resultGet?.type,
        location.pathname,
      );
      auth?.setProgressSteps(initialProgressSteps);
    }
  }, []);

  /**
   * Update progress steps when we get test data
   */
  useEffect(() => {
    if (resultGet) {
      const newProgressSteps = calculateProgressSteps(
        resultGet,
        resultGet?.type,
        location.pathname,
        auth,
      );
      auth?.setProgressSteps(newProgressSteps);
    }
  }, [resultGet]);

  useEffect(() => {
    localStorage.setItem("isLoggedIn", true);
  });

  /**
   * Validates the input values based on the current path.
   * Returns appropriate validation messages for each test type.
   */

  const validationMessages = {
    blood:
      "To proceed with activating your Blood Test Kit, please ensure that you have selected the appropriate option on the activation page.",
    sh_banned: (
      <>
        There is a problem with your test activation. You will require a
        replacement kit - please click this link to order a free of charge
        replacement{" "}
        <a
          href="https://myhealthchecked.com/product/male-sexual-health-test"
          target="_blank"
          rel="noopener noreferrer"
          className="replacement-link"
        >
          https://myhealthchecked.com/product/male-sexual-health-test
        </a>{" "}
        and use discount code "REPLACEMSH2025" at checkout.
        {"\n\n"}
        <br />
        If there are any other issues or you would like us to order the
        replacement for you please contact{" "}
        <a href="mailto:care@myhealthchecked.com" className="replacement-link">
          care@myhealthchecked.com
        </a>
      </>
    ),
    sh: "To proceed with activating your Sexual Health Test Kit, please ensure that you have selected the appropriate option on the activation page.",
    dna: "Secondary_barcode must be null.",
    blue_label:
      "Oops! That's the blue label barcode. Please enter the one with gold label instead.",
    lavender_label:
      "Oops! That's the lavender label barcode. Please enter the one with gold label instead.",
  };

  /**
   * Check if barcode is a blue label (starts with MBL or MBN)
   */
  const isBlueLabelBarcode = (code) => {
    if (!code) return false;
    const upperCode = code?.toUpperCase();
    return upperCode.startsWith("MB");
  };

  const isLavenderLabelBarcode = (code) => {
    if (!code) return false;
    const upperCode = code?.toUpperCase();
    return upperCode.startsWith("ML");
  };

  /**
   * Check if barcode is a lavender label and linked to gold
   */
  const isLavenderLinkedToGold = (resultData) => {
    return (
      resultData?.secondary_barcode_type === "lavender" &&
      resultData?.has_gold_barcode === true
    ); // Assuming API returns this flag
  };

  /**
   * Auto-format DNA barcode by removing hyphens when CO or BT is typed
   */
  const formatDNABarcode = (code) => {
    if (!code) return code;
    const upperCode = code?.toUpperCase();

    // Format CO or BT barcodes by removing hyphens
    if (upperCode.startsWith("CO") || upperCode.startsWith("BT")) {
      return code.replace(/-/g, "");
    }
    return code;
  };

  const validateSHTest = (values) => {
    if (values.banned_barcode === true) {
      return validationMessages.sh_banned;
    }
    const isUrineValid = values.secondary_barcode_type === "urine";
    if (
      !values.primary_barcode_id?.startsWith("RG") &&
      (!values.secondary_barcode_id || !isUrineValid)
    ) {
      return validationMessages.sh;
    }
    return "";
  };

  /**
   * Enhanced validation that includes new barcode label rules
   */
  const handleValidation = (values, currentPath, inputCode = "") => {
    // Check for blue label barcode
    if (isBlueLabelBarcode(inputCode)) {
      return validationMessages.blue_label;
    }

    // Check for lavender label barcode linked to gold
    if (isLavenderLinkedToGold(values)) {
      return validationMessages.lavender_label;
    }

    if (currentPath.includes("sex")) {
      return validateSHTest(values);
    }
    if (currentPath.includes("dna") && values.secondary_barcode_id) {
      return validationMessages.dna;
    }
    return "";
  };

  useEffect(() => {
    if (serverError) {
      setError(serverError);
    }
  }, [serverError]);

  const handleCodeBlur = async (code, setFieldValue) => {
    // Ensure code is a string and trim whitespace
    const trimmedCode = code?.toString().trim();

    if (!trimmedCode) {
      return;
    }

    // Clear previous errors when user starts typing
    setError("");

    // Auto-format DNA barcodes
    const formattedCode = formatDNABarcode(trimmedCode);

    // Check if formatDNABarcode returned a validation message
    if (formattedCode === validationMessages.blood) {
      setError(formattedCode);
      return;
    }

    let finalCode = trimmedCode;
    if (formattedCode !== trimmedCode) {
      setFieldValue("code", formattedCode);
      finalCode = formattedCode;
    }

    // Check for blue label barcode immediately and prevent API call
    if (isBlueLabelBarcode(finalCode) || isLavenderLabelBarcode(finalCode)) {
      if (isBlueLabelBarcode(finalCode)) {
        setError(validationMessages.blue_label);
      } else if (isLavenderLabelBarcode(finalCode)) {
        setError(validationMessages.lavender_label);
      }
      return;
    }

    try {
      const apiUrl = `/api/dashboard/check-test/${finalCode?.toUpperCase()}/`;

      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("access_token")}`,
        },
      });

      // With axios, successful responses are in response.data
      const data = response.data;
      barcodeData = data;

      // Update progress steps when we get barcode data
      const newProgressSteps = calculateProgressSteps(
        data,
        data?.type,
        location.pathname,
      );
      auth?.setProgressSteps(newProgressSteps);

      if (data?.postage_tracking) {
        const formattedTracking = formatTrackingNumber(data.postage_tracking);
        setFieldValue("trackingNumber", formattedTracking);
      }
    } catch (err) {
      // Only show fetch errors for non-blue label barcodes
      if (
        err?.response?.data?.session_expired ||
        err?.response?.data?.unauthorized
      ) {
        callApi(null, logoutUrl, "POST");
        auth.logout();
        auth?.showToast(
          "Error",
          "You have logged in to another device",
          "../assets/mhc.png",
          <XSquare />,
        );
      }
      if (
        !isBlueLabelBarcode(finalCode) &&
        !isLavenderLabelBarcode(finalCode)
      ) {
        setError(
          err?.response?.data?.message ||
            err.message ||
            "Error fetching tracking number",
        );
      }
    }
  };

  const proceedWithSubmission = (values) => {
    const formattedCode = formatDNABarcode(values.code);

    if (formattedCode === validationMessages.blood) {
      setError(formattedCode);
      return;
    }

    const submitData = { ...values, code: formattedCode };
    setData(submitData);

    const getUrl = `/api/dashboard/check-test/${submitData.code?.toUpperCase()}/`;
    callApi({}, getUrl, "GET");
  };

  const handleSubmit = async (values) => {
    setError("");

    if (isBlueLabelBarcode(values.code)) {
      setError(validationMessages.blue_label);
      return;
    }

    if (values?.code?.toUpperCase().startsWith("CONC")) {
      setPendingValues(values);
      setModalOpen(true);
      return;
    }
    proceedWithSubmission(values);
  };

  // Auto-submit consent when we get test kit data
  useEffect(() => {
    if (resultGet?.label_issue && !isConsented) {
      setShow(true);
    }
    if (
      resultGet?.id &&
      ((isConsented && resultGet?.label_issue) || !resultGet?.label_issue) &&
      !consentSubmitted
    ) {
      const validationError = handleValidation(
        resultGet,
        resultGet?.type,
        data?.code,
      );
      if (validationError) {
        setError(validationError);
        return;
      }

      // First, submit the barcode activation
      const postPayload = {
        code:
          resultGet?.primary_barcode_id?.toUpperCase() ||
          resultGet?.secondary_barcode_id?.toUpperCase(),
        activation_stage: "barcode",
        tracking_id: data?.trackingNumber,
      };
      callApi(postPayload, "/api/dashboard/activating/", "POST");
    }
  }, [resultGet, isConsented, consentSubmitted]);

  // Auto-submit consent after barcode activation
  useEffect(() => {
    if (
      resultPost &&
      resultGet?.id &&
      !consentSubmitted &&
      (resultGet?.label_issue === false ||
        (isConsented && resultGet?.label_issue))
    ) {
      // Auto-submit consent
      const consentPayload = {
        test_kit_id: resultGet?.test_kit_id,
        consent_given: true,
        ...((resultGet?.type?.startsWith("dna") ||
          resultGet?.type?.startsWith("boots-dna-")) && {
          consent_type: "processing",
        }),
      };

      setConsentSubmitted(true);

      // Submit consent activation stage first
      const consentActivationPayload = {
        activation_stage: "barcode",
        code: resultGet?.primary_barcode_id || resultGet?.secondary_barcode_id,
      };

      // Submit both consent and activation stage
      Promise.all([
        callApi(consentPayload, "/api/dashboard/consent/", "POST"),
        callApi(consentActivationPayload, "/api/dashboard/activating/", "POST"),
      ]).then(() => {});
    }
  }, [resultPost, resultGet, consentSubmitted, isConsented]);

  // Navigate to confirm-details after consent is submitted
  useEffect(() => {
    if (consentSubmitted && resultPost) {
      // Update progress steps after successful completion
      const completedProgressSteps = updateProgressAfterCompletion(
        resultGet,
        resultGet?.type,
        location.pathname,
      );
      auth?.setProgressSteps(completedProgressSteps);

      const navigateTo =
        resultGet?.secondary_barcode_id && resultGet?.primary_barcode_id
          ? `/secondary-barcode/${
              resultGet.primary_barcode_id || resultGet?.secondary_barcode_id
            }`
          : `/confirm-details/${
              resultGet?.primary_barcode_id || resultGet?.secondary_barcode_id
            }`;

      navigate(navigateTo);
    }
  }, [consentSubmitted, resultPost, resultGet]);

  useEffect(() => {
    if (resultGet) {
      localStorage.setItem("primary_barcode_id", resultGet.primary_barcode_id);
      localStorage.setItem(
        "secondary_barcode_id",
        resultGet.secondary_barcode_id || null,
      );
    }
  }, [resultGet]);

  const getFormFields = () => {
    const path = location?.pathname;
    if (path.includes("blood")) return bloodFields;
    if (path.includes("sh")) return shFields;
    if (path.includes("dna")) return dnaFields;
    return [];
  };

  if (error === networkError) {
    return <ErrorDisplay error={error} consent={false} />;
  }

  return (
    <div className="row justify-content-center enter-barcode-page">
      <div className="col-md-12">
        <div className="info-banner">
          <div className="d-flex info-header">
            <img src={InfoIcon} alt="information icon"/>
            <h1 className="ageo-semibold fs-20 mt-0">Your Test Registration Requires a Few Simple Steps</h1>
          </div>
          <p className="ageo-semibold fs-20 subtitle mb-0">After submitting your sample ID below, the next page will guide you through this process</p>
        </div>
        <StaticModal
          show={show}
          handleClick={handleClick}
          handleClose={() => setShow(false)}
        />
        {/* Display loading spinner if the request is in progress */}
        {isLoading && <div>Loading...</div>}
        <DynamicForm
          trackingNumber={resultGet?.postage_tracking}
          handleCodeBlur={handleCodeBlur}
          fields={activateKitFields || getFormFields()}
          validationSchema={barcodeValidationSchema}
          className={"barcode-form"}
          initialValues={{ ...data }}
          onSubmit={handleSubmit}
          validationError={error}
        />
      </div>
      <BarcodeConfirmationModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        title="Service Update"
        bodyContent={
          <div className="enter-bardcode-confirmation-modal">
            <p>
              Following a recent service update, your test results may take up to 28
              days to process. We'll always aim to deliver your results sooner
              whenever possible, and notify you as soon as your results are ready.
            </p>
            <p>
             If you have any concerns, please exit registration and contact our Customer Care team at{" "}
              <a className="mail-href" href="mailto:care@myhealthchecked.com">care@myhealthchecked.com</a>.
            </p>
          </div>
        }
        cancelLabel="Exit registration"
        confirmLabel="I understand, continue to register"
        cancelClassName="btn-green barcode-confirmation-btn"
        confirmClassName="btn-primary barcode-confirmation-btn"
        onCancel={() => {
          setModalOpen(false);
          setPendingValues(null);
        }}
        onConfirm={() => {
          setModalOpen(false);
          proceedWithSubmission(pendingValues);
          setPendingValues(null);
        }}
      />
      {serverError ? <span className="d-none">{serverError}</span> : <></>}
    </div>
  );
};

export default EnterBarcode;
